import { useState, useEffect } from "react";
import {
  Calendar,
  User,
  MapPin,
  MessageSquare,
  Clock4,
  SquarePen,
  Phone,
  Paperclip,
  Video,
  X,
  XCircle,
  CheckCircle2,
  FileText,
  Star,
} from "lucide-react";
import { formatDate } from "../../utils/formatDate";
import {
  renderStatusBadge,
  renderCategoryBadgeStyled,
  renderUrgencyBadge,
  getStatusStyle,
} from "../../utils/badgeUtils";
import { useReports } from "../../hooks/useReports";
import { showToast } from "../../utils/toastNotification";
import { validateReport } from "../../validator/reportValidator";
import dayjs from "dayjs";
import MediaGallery from "./MediaGallery";
import BaseModal, { ModalFooter } from "../base/BaseModal";
import CustomSelect from "../base/CustomSelect";
import { utcToVietnamTime, vietnamTimeToUTC } from "../../utils/datePicker";
import { ConfigProvider, Upload } from "antd";
import viVN from "antd/locale/vi_VN";
import "antd/dist/reset.css";
import { usePermission } from "../../hooks/usePermission";
import { uploadVideoWithHls } from "../../services/videoUploadClient";
import ReportAttachmentList from "./ReportAttachmentList";
import { ExtensionService } from "../../services/extensionService";

const RESOLVE_ATTACHMENT_ACCEPT = ".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx";
const RESOLVE_ATTACHMENT_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const isImageAttachment = (file) => file?.dinh_dang_file?.startsWith("image/");

const ReportDetailModal = ({
  isOpen,
  onClose,
  report,
  loading = false,
  onStatusUpdated,
  mode = "edit",
  onModeChange,
  onOpenApprove,
  onOpenReject,
}) => {
  const { statusReport, updateStatus, clearError } = useReports();
  const [selectedStatus, setSelectedStatus] = useState("");
  const [responseContent, setResponseContent] = useState("");
  const [expectedResponseDate, setExpectedResponseDate] = useState(null);
  const [expectedCompletionDate, setExpectedCompletionDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);
  // Video hiện trường đã xử lý (1 video, upload chunked + HLS)
  const [resolveVideo, setResolveVideo] = useState(null); // { id, final_hls_url } khi DONE
  const [videoFileName, setVideoFileName] = useState("");
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoPhase, setVideoPhase] = useState(""); // uploading | processing | done
  const [extensionList, setExtensionList] = useState([]);
  const { canUpdate } = usePermission();
  const isEditMode = mode === "edit";

  // Trạng thái "Đã giải quyết" → bắt buộc đính kèm ảnh hiện trường (khớp ràng buộc BE)
  const isResolvedStatus =
    (selectedStatus || "").trim().toLowerCase() === "đã giải quyết";

  useEffect(() => {
    if (isOpen && report) {
      const searchKey = report.ma_phan_anh || report.id;
      if (searchKey) {
        ExtensionService.getAll({ search: searchKey })
          .then((res) => {
            if (Array.isArray(res)) {
              setExtensionList(res);
            }
          })
          .catch(() => setExtensionList([]));
      }
    } else {
      setExtensionList([]);
    }
  }, [isOpen, report]);

  const getExtensionReason = (item) => {
    if (item?.ly_do_gia_han) return item.ly_do_gia_han;
    if (item?.ly_do) return item.ly_do;
    if (report?.ly_do_gia_han) return report.ly_do_gia_han;
    if (report?.de_nghi_gia_han_phan_anh) {
      if (Array.isArray(report.de_nghi_gia_han_phan_anh) && report.de_nghi_gia_han_phan_anh.length > 0) {
        const match = report.de_nghi_gia_han_phan_anh.find((e) => e.ly_do_gia_han || e.ly_do);
        if (match) return match.ly_do_gia_han || match.ly_do;
      } else if (typeof report.de_nghi_gia_han_phan_anh === "object") {
        return report.de_nghi_gia_han_phan_anh.ly_do_gia_han || report.de_nghi_gia_han_phan_anh.ly_do;
      }
    }
    if (extensionList && extensionList.length > 0) {
      const matched = extensionList.find(
        (ext) =>
          ext.id_phan_anh === report?.id ||
          ext.ma_phan_anh === report?.ma_phan_anh ||
          ext.phan_anh?.id === report?.id
      );
      if (matched?.ly_do_gia_han) return matched.ly_do_gia_han;
      if (matched?.reason) return matched.reason;
    }
    if (report?.reason) return report.reason;
    return null;
  };

  const handleEditMode = () => {
    if (onModeChange) {
      onModeChange("edit");
    }
  };

  useEffect(() => {
    if (isOpen && report) {
      if (report.lich_su_trang_thai && report.lich_su_trang_thai.length > 0) {
        const latest = report.lich_su_trang_thai.reduce((prev, curr) => {
          try {
            return dayjs(prev.thoi_gian_tao).isSameOrAfter(
              dayjs(curr.thoi_gian_tao),
            )
              ? curr
              : prev;
          } catch (e) {
            return prev;
          }
        }, report.lich_su_trang_thai[0]);

        const latestName = latest && latest.ten ? latest.ten : "";
        setSelectedStatus(latestName);

        setResponseContent(latest?.ghi_chu || "");
      }

      setExpectedResponseDate(
        utcToVietnamTime(report.thoi_gian_phan_hoi_du_kien),
      );
      setExpectedCompletionDate(
        utcToVietnamTime(report.ngay_du_kien_hoan_thanh),
      );
      setFileList([]);
      setResolveVideo(null);
      setVideoFileName("");
      setVideoProgress(0);
      setVideoPhase("");
      setVideoUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.id, statusReport, isOpen]);

  const handleUpdateStatus = async () => {
    try {
      setIsSubmitting(true);
      clearError();

      const formData = {
        selectedStatus,
        responseContent,
        expectedResponseDate: expectedResponseDate
          ? expectedResponseDate.toDate()
          : null,
        expectedCompletionDate: expectedCompletionDate
          ? expectedCompletionDate.toDate()
          : null,
      };

      const { isValid, errors } = await validateReport(formData);

      if (!isValid) {
        const firstError = Object.values(errors)[0];
        showToast.error(firstError);
        setIsSubmitting(false);
        return;
      }

      const files = fileList.map((f) => f.originFileObj).filter(Boolean);

      // Đang tải video → chặn submit cho tới khi xử lý xong
      if (videoUploading) {
        showToast.error("Video đang được tải lên, vui lòng đợi xử lý xong");
        setIsSubmitting(false);
        return;
      }

      // Bat buoc >=1 tep HOAC >=1 video hien truong khi giai quyet phan anh
      if (isResolvedStatus && files.length === 0 && !resolveVideo) {
        showToast.error(
          "Phai dinh kem it nhat 1 tep hoac video hien truong khi giai quyet phan anh",
        );
        setIsSubmitting(false);
        return;
      }

      const statusData = {
        trangThai: selectedStatus,
        ghiChu: responseContent,
      };

      if (resolveVideo?.id) {
        statusData.idVideoGiaiQuyet = [resolveVideo.id];
      }

      if (expectedResponseDate) {
        statusData.thoiGianPhanHoiDuKien =
          vietnamTimeToUTC(expectedResponseDate);
      }

      if (
        expectedCompletionDate &&
        (selectedStatus === "DANG_XU_LY" || selectedStatus === "Đang xử lý")
      ) {
        statusData.ngayDuKienHoanThanh = vietnamTimeToUTC(
          expectedCompletionDate,
        );
      }

      if (files.length > 0) {
        statusData.files = files;
      }

      await updateStatus(report.id, statusData);
      showToast.success("Cập nhật trạng thái thành công");
      setSelectedStatus("");
      setResponseContent("");
      setExpectedResponseDate(null);
      setExpectedCompletionDate(null);
      setFileList([]);
      setResolveVideo(null);
      setVideoFileName("");
      setVideoProgress(0);
      setVideoPhase("");

      if (onStatusUpdated) {
        onStatusUpdated();
      }

      onClose();
    } catch (error) {
      if (error?.message) {
        showToast.error(error.message);
      }

      if (
        error?.errors &&
        Array.isArray(error.errors) &&
        error.errors.length > 0
      ) {
        error.errors.forEach((err) => {
          if (err?.message) {
            showToast.error(err.message);
          }
        });
      } else if (typeof error === "string") {
        showToast.error(error);
      } else if (!error?.message && !error?.errors) {
        showToast.error("Có lỗi xảy ra khi cập nhật trạng thái");
      }

      clearError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectResolveVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // cho phép chọn lại cùng tên file
    if (!file) return;

    if (!file.type.startsWith("video/")) {
      showToast.error("Chỉ chấp nhận tệp video");
      return;
    }
    const maxMB = 300;
    if (file.size / 1024 / 1024 > maxMB) {
      showToast.error(`Video tối đa ${maxMB}MB`);
      return;
    }

    try {
      setVideoUploading(true);
      setResolveVideo(null);
      setVideoFileName(file.name);
      setVideoProgress(0);
      setVideoPhase("uploading");

      const result = await uploadVideoWithHls(file, {
        onProgress: (percent, phase) => {
          setVideoProgress(percent);
          setVideoPhase(phase);
        },
      });

      setResolveVideo(result);
      setVideoPhase("done");
      showToast.success("Tải video lên thành công");
    } catch (err) {
      setResolveVideo(null);
      setVideoFileName("");
      setVideoPhase("");
      showToast.error(err?.message || "Tải video thất bại");
    } finally {
      setVideoUploading(false);
    }
  };

  const removeResolveVideo = () => {
    if (videoUploading) return;
    setResolveVideo(null);
    setVideoFileName("");
    setVideoProgress(0);
    setVideoPhase("");
  };

  if (!isOpen) return null;

  const API_URL = process.env.REACT_APP_API_URL;

  // Mốc tiếp nhận/bắt đầu xử lý: thời điểm + người tiếp nhận (từ lịch sử trạng thái)
  const tiepNhan = (report?.lich_su_trang_thai || []).find(
    (l) => l.ten === "Đang xử lý",
  );
  const tiepNhanThoiGian =
    tiepNhan?.thoi_gian_tao || report?.thoi_gian_tiep_nhan;
  const tiepNhanNguoi =
    tiepNhan?.nguoi_dung?.ho_va_ten || tiepNhan?.nguoi_dung?.ten_dang_nhap;
  const ngayDuKienHoanThanh =
    report?.ngay_du_kien_hoan_thanh ||
    report?.thoi_gian_phan_hoi_du_kien ||
    report?.currentDeadline;
  const isGiaHan =
    report?.is_gia_han ||
    report?.isGiaHan ||
    report?.phan_anh?.is_gia_han ||
    (Array.isArray(report?.de_nghi_gia_han_phan_anh) &&
      report.de_nghi_gia_han_phan_anh.some(
        (e) => e.trang_thai === "APPROVED" || e.trang_thai === "Đã duyệt",
      )) ||
    (Array.isArray(extensionList) &&
      extensionList.some(
        (e) => e.status === "APPROVED" || e.trang_thai === "APPROVED",
      ));

  // Tách ảnh đính kèm: ảnh phản ánh gốc (loai !== 'GIAI_QUYET') vs ảnh hiện trường đã xử lý
  const attachments = report?.dinh_kem_phan_anh || [];
  const reportAttachments = attachments.filter((a) => a.loai !== "GIAI_QUYET");
  const resolveAttachments = attachments.filter((a) => a.loai === "GIAI_QUYET");
  const reportImages = reportAttachments.filter(isImageAttachment);
  const reportDocuments = reportAttachments.filter(
    (a) => !isImageAttachment(a),
  );
  const resolveImages = resolveAttachments.filter(isImageAttachment);
  const resolveDocuments = resolveAttachments.filter(
    (a) => !isImageAttachment(a),
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${isEditMode ? "Phê duyệt / Xử lý phản ánh" : "Chi tiết phản ánh"}: #${report?.ma_phan_anh || ""}`}
      subtitle={
        isEditMode ? "Phê duyệt, tiếp nhận hoặc cập nhật tiến độ giải quyết phản ánh" : undefined
      }
      size="3xl"
      className="max-w-5xl"
      footer={
        isEditMode ? (
          <ModalFooter
            onCancel={onClose}
            onSubmit={handleUpdateStatus}
            cancelText="Hủy"
            submitText="Lưu và gửi thông báo"
            submitDisabled={isSubmitting || videoUploading}
            submitLoading={isSubmitting}
          />
        ) : (
          <div className="flex items-center justify-between w-full">
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-gray-300 shadow-xs px-4 py-2 bg-white text-xs md:text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none cursor-pointer"
              onClick={onClose}
            >
              Đóng
            </button>

            {(() => {
              const latestStatus = report?.lich_su_trang_thai?.[0]?.ten || report?.trang_thai || '';
              const isPending = latestStatus === 'Đã gửi' || latestStatus === 'DA_GUI';

              if (!isPending) return null;

              return (
                <div className="flex items-center gap-2.5">
                  {onOpenReject && (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onOpenReject(report);
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs md:text-sm font-bold shadow-xs transition-colors cursor-pointer"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>Từ chối</span>
                    </button>
                  )}
                  {onOpenApprove && (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onOpenApprove(report);
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs md:text-sm font-bold shadow-xs transition-colors cursor-pointer"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Phê duyệt</span>
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        )
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
          <span className="ml-3 text-gray-600">Đang tải dữ liệu...</span>
        </div>
      ) : report ? (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto overflow-x-hidden px-1 -mx-1 break-words">
          <div className="flex-1">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 leading-snug tracking-tight mb-3 break-words overflow-wrap-anywhere">
              {report.tieu_de}
            </h3>
            <div className="flex items-center gap-2 mb-4">
              {renderStatusBadge(report.lich_su_trang_thai)}
              {renderUrgencyBadge(report.muc_do)}
              {renderCategoryBadgeStyled(report.linh_vuc_phan_anh)}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-gray-600">Ngày gửi:</span>
              <span className="text-sm text-gray-600">
                {formatDate(report.thoi_gian_tao)}
              </span>
            </div>
            {report.ten_nguoi_phan_anh && (
              <div className="flex items-start gap-2 min-w-0 flex-shrink">
                <User className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-600 flex-shrink-0">
                  Người gửi: {report.ten_nguoi_phan_anh}
                </span>
              </div>
            )}
            {report.sdt_nguoi_phan_anh && (
              <div className="flex items-start gap-2 min-w-0 flex-shrink">
                <Phone className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-600 flex-shrink-0">
                  Số điện thoại: {report.sdt_nguoi_phan_anh}
                </span>
              </div>
            )}
          </div>

          <div className="bg-blue-50 rounded-md px-3 py-2.5 text-sm text-gray-700 space-y-1.5">
            {tiepNhanThoiGian ? (
              <>
                <div className="flex items-center gap-2">
                  <Clock4 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>
                    Đã tiếp nhận lúc{" "}
                    <span className="font-medium text-gray-900">
                      {formatDate(tiepNhanThoiGian)}
                    </span>
                  </span>
                </div>
                {tiepNhanNguoi && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>
                      Cán bộ tiếp nhận xử lý:{" "}
                      <span className="font-bold text-gray-900">
                        {tiepNhanNguoi}
                      </span>
                    </span>
                  </div>
                )}
                {ngayDuKienHoanThanh && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>
                      Ngày dự kiến hoàn thành:{" "}
                      <span className="font-bold text-gray-900">
                        {formatDate(ngayDuKienHoanThanh)}
                      </span>
                      {isGiaHan && (
                        <span className="ml-1.5 text-xs font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded border border-amber-200 inline-flex items-center">
                          Đã gia hạn
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Clock4 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>Chưa tiếp nhận</span>
                </div>
                {ngayDuKienHoanThanh && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>
                      Ngày dự kiến hoàn thành:{" "}
                      <span className="font-bold text-gray-900">
                        {formatDate(ngayDuKienHoanThanh)}
                      </span>
                      {isGiaHan && (
                        <span className="ml-1.5 text-xs font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded border border-amber-200 inline-flex items-center">
                          Đã gia hạn
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              Mô tả chi tiết
            </h4>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-700 whitespace-pre-wrap break-words overflow-wrap-anywhere word-break-break-word">
                {report.mo_ta || "Không có mô tả"}
              </p>
            </div>
          </div>

          <MediaGallery
            title="Ảnh phản ánh"
            images={reportImages}
            videos={report.videos || []}
            apiUrl={API_URL}
          />
          <ReportAttachmentList
            title="Tai lieu phan anh"
            files={reportDocuments}
            apiUrl={API_URL}
          />

          {(resolveImages.length > 0 ||
            (report.videos_giai_quyet || []).length > 0) && (
            <MediaGallery
              title="Ảnh/Video hiện trường đã xử lý"
              images={resolveImages}
              videos={report.videos_giai_quyet || []}
              apiUrl={API_URL}
            />
          )}
          <ReportAttachmentList
            title="Tài liệu xử lý phản ánh"
            files={resolveDocuments}
            apiUrl={API_URL}
          />

          <div className="border-b pb-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Vị trí & Khu phố
            </h4>
            <div className="bg-gray-100 p-3.5 rounded-md space-y-1">
              <p className="text-sm text-gray-700 break-words overflow-wrap-anywhere">
                <span className="font-medium text-gray-900">Địa chỉ:</span> {report?.vi_tri || "Không có thông tin vị trí"}
              </p>
              {report?.khu_pho && (
                <p className="text-sm text-gray-700 break-words overflow-wrap-anywhere">
                  <span className="font-medium text-gray-900">Khu phố:</span> {report.khu_pho}
                </p>
              )}
            </div>
          </div>

          {/* ĐÁNH GIÁ CỦA NGƯỜI DÂN */}
          {(() => {
            const danhGia =
              Array.isArray(report?.danh_gia_phan_anh) &&
              report.danh_gia_phan_anh.length > 0
                ? report.danh_gia_phan_anh[0]
                : report?.danh_gia_phan_anh?.diem
                ? report.danh_gia_phan_anh
                : null;

            if (!danhGia) return null;

            const scoreDesc = {
              5: "Rất hài lòng",
              4: "Hài lòng",
              3: "Bình thường",
              2: "Chưa hài lòng",
              1: "Rất chưa hài lòng",
            }[danhGia.diem] || "";

            return (
              <div className="bg-amber-50/80 border border-amber-200 rounded-lg p-3.5 space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-amber-900 flex items-center gap-1.5">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    Đánh giá của người dân
                  </h4>
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full">
                    ✓ Đã đánh giá
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        size={16}
                        className={
                          s <= Number(danhGia.diem)
                            ? "text-amber-400 fill-amber-400"
                            : "text-gray-200 fill-gray-200"
                        }
                      />
                    ))}
                  </div>
                  <span className="text-sm font-bold text-gray-900">
                    {danhGia.diem}/5 sao
                  </span>
                  {scoreDesc && (
                    <span className="text-xs text-gray-500">({scoreDesc})</span>
                  )}
                </div>
                {danhGia.nhan_xet && (
                  <div className="bg-white/90 border border-amber-100 p-2.5 rounded text-xs text-gray-700 italic">
                    "{danhGia.nhan_xet}"
                  </div>
                )}
                {danhGia.thoi_gian_tao && (
                  <p className="text-[11px] text-gray-400">
                    Thời gian đánh giá: {formatDate(danhGia.thoi_gian_tao)}
                  </p>
                )}
              </div>
            );
          })()}

          {!isEditMode && (
            <>
              <div className="">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    Trạng thái hiện tại
                  </h4>
                  <div
                    className={`flex items-center gap-2 bg-blue-600 ${!canUpdate("PA") ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700 focus:ring-blue-500"} text-white px-4 py-2 rounded-md text-sm font-medium`}
                    disabled={!canUpdate("PA")}
                  >
                    <SquarePen className="w-4 h-4 flex-shrink-0" />
                    <button
                      disabled={!canUpdate("PA")}
                      onClick={handleEditMode}
                    >
                      Cập nhật trạng thái
                    </button>
                  </div>
                </div>
                <p>{renderStatusBadge(report.lich_su_trang_thai)}</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock4 className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-900 leading-none">
                    Lịch sử cập nhật ({report.lich_su_trang_thai?.length || 0})
                  </span>
                </div>

                {report.lich_su_trang_thai &&
                report.lich_su_trang_thai.length > 0 ? (
                  <div className="relative pl-6">
                    <div className="absolute left-2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                    {report.lich_su_trang_thai.map((item, index) => {
                      const statusStyle = getStatusStyle(item.ten);
                      const isLatest = index === 0;

                      return (
                        <div key={item.id || index} className="mb-2 relative">
                          <span
                            className={`absolute -left-6 top-1.5 w-4 h-4 rounded-full border-2 ${
                              isLatest
                                ? "bg-blue-600 border-blue-600"
                                : "bg-white border-gray-400"
                            }`}
                          ></span>

                          <div className="p-2.5 bg-gray-50 ml-2 rounded-md border border-gray-100">
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                              <span
                                className="px-3 py-0.5 text-xs font-semibold rounded-full border"
                                style={{
                                  backgroundColor: statusStyle.bg,
                                  color: statusStyle.color,
                                  borderColor: (statusStyle.color || "#374151") + "30",
                                }}
                              >
                                {item.ten}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(item.thoi_gian_tao)}
                              </span>
                            </div>

                            {item.nguoi_dung && (
                              <p className="text-sm text-gray-700 mb-1">
                                <User className="inline-block mr-2 text-gray-400 w-4 h-4" />
                                <span className="font-medium">
                                  {item.nguoi_dung.ho_va_ten ||
                                    item.nguoi_dung.ten_dang_nhap ||
                                    "Người thực hiện"}
                                </span>
                              </p>
                            )}

                            {item.ghi_chu && (
                              <p className="text-sm text-gray-700 break-words overflow-wrap-anywhere">
                                <MessageSquare className="inline-block mr-2 text-gray-400 w-4 h-4 align-text-top" />
                                <span>{item.ghi_chu}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    Không có lịch sử cập nhật
                  </div>
                )}
              </div>
            </>
          )}
          {isEditMode && (
            <>
              <div className="mt-0">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 required-label">
                  Phê duyệt / Cập nhật trạng thái phản ánh
                </h4>
                <div className="space-y-3">
                  <div>
                    <CustomSelect
                      value={selectedStatus}
                      onChange={(val) => setSelectedStatus(typeof val === 'object' ? val?.target?.value : val)}
                      disabled={!isEditMode}
                      placeholder="Chọn trạng thái"
                      options={
                        statusReport
                          ? Object.entries(statusReport)
                              .filter(
                                ([key, value]) =>
                                  key !== "TU_CHOI" &&
                                  value !== "Từ chối" &&
                                  value !== "TU_CHOI" &&
                                  key !== "DONG" &&
                                  value !== "Đóng" &&
                                  value !== "DONG",
                              )
                              .map(([key, value]) => ({
                                value: value,
                                label: value
                              }))
                          : []
                      }
                    />
                  </div>
                  {isResolvedStatus && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tệp hiện trường đã xử lý (tối thiểu 1 tệp hoặc 1 video)
                      </label>
                      <Upload
                        accept={RESOLVE_ATTACHMENT_ACCEPT}
                        multiple
                        maxCount={5}
                        fileList={fileList}
                        beforeUpload={(file) => {
                          const isSupported =
                            RESOLVE_ATTACHMENT_MIME_TYPES.includes(file.type);
                          if (!isSupported) {
                            showToast.error(
                              "Chỉ chấp nhận JPG, PNG, PDF, DOC, DOCX, XLS, XLSX",
                            );
                            return Upload.LIST_IGNORE;
                          }
                          const isLt10M = file.size / 1024 / 1024 <= 10;
                          if (!isLt10M) {
                            showToast.error("Mỗi tệp tối đa 10MB");
                            return Upload.LIST_IGNORE;
                          }
                          return false; // khong tu upload, giu file de gui cung FormData
                        }}
                        onChange={({ fileList: newList }) =>
                          setFileList(newList.slice(0, 5))
                        }
                      >
                        {fileList.length >= 5 ? null : (
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            <Paperclip className="w-4 h-4" />
                            Chọn tệp
                          </button>
                        )}
                      </Upload>
                      <p className="text-xs text-gray-500 mt-1">
                        Tối đa 5 tệp: JPG, PNG, PDF, DOC, DOCX, XLS, XLSX. Mỗi
                        tệp tối đa 10MB.
                      </p>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Video hiện trường đã xử lý
                        </label>

                        {!resolveVideo && !videoUploading && (
                          <label className="inline-flex flex-col items-center justify-center w-28 h-24 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                            <Video className="w-5 h-5 text-gray-500" />
                            <span className="text-xs mt-1">Tải video</span>
                            <input
                              type="file"
                              accept="video/*"
                              className="hidden"
                              onChange={handleSelectResolveVideo}
                            />
                          </label>
                        )}

                        {videoUploading && (
                          <div className="text-sm text-gray-600">
                            <p className="truncate max-w-xs">{videoFileName}</p>
                            <p className="mt-0.5">
                              {videoPhase === "processing"
                                ? "Đang xử lý video (HLS)…"
                                : `Đang tải lên… ${videoProgress}%`}
                            </p>
                            <div className="w-48 h-1.5 bg-gray-200 rounded mt-1 overflow-hidden">
                              <div
                                className="h-full bg-blue-500 transition-all"
                                style={{ width: `${videoProgress}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {resolveVideo && !videoUploading && (
                          <div className="inline-flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                            <Video className="w-4 h-4" />
                            <span className="truncate max-w-[180px]">
                              {videoFileName || "Video đã tải lên"}
                            </span>
                            <button
                              type="button"
                              onClick={removeResolveVideo}
                              className="text-gray-400 hover:text-red-500"
                              title="Xóa video"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}

                        <p className="text-xs text-gray-500 mt-1">
                          Tối đa 1 video (≤ 100MB). Video được xử lý HLS — vui
                          lòng đợi hoàn tất trước khi lưu.
                        </p>
                      </div>

                      <p className="text-xs text-amber-600 mt-2">
                        Bắt buộc tối thiểu 1 tệp{" "}
                        <span className="font-medium">hoặc</span> 1 video hiện
                        trường.
                      </p>
                    </div>
                  )}
                  {isEditMode && (
                    <ConfigProvider locale={viVN}>
                      {/* <div>
                                                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${selectedStatus !== 'Đã giải quyết' && selectedStatus !== 'Đóng' ? 'required-label' : ''}`}>
                                                        Thời gian phản hồi dự kiến
                                                    </label>
                                                    <DateTimePicker
                                                        value={expectedResponseDate}
                                                        onChange={(date) => setExpectedResponseDate(date)}
                                                        placeholder="Chọn thời gian phản hồi"
                                                    />
                                                </div>

                                                <div>
                                                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${selectedStatus !== 'Đã giải quyết' && selectedStatus !== 'Đóng' ? 'required-label' : ''}`}>
                                                        Ngày dự kiến hoàn thành
                                                    </label>
                                                    <DateTimePicker
                                                        value={expectedCompletionDate}
                                                        onChange={(date) => setExpectedCompletionDate(date)}
                                                        placeholder="Chọn ngày hoàn thành"
                                                    />
                                                </div> */}
                    </ConfigProvider>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nội dung phản hồi
                    </label>
                    <textarea
                      placeholder="Nhập nội dung phản hồi cho người dân..."
                      rows="3"
                      disabled={!isEditMode}
                      value={responseContent}
                      onChange={(e) => setResponseContent(e.target.value)}
                      className={`w-full px-3 py-2 text-gray-700 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none break-words overflow-wrap-anywhere word-break-break-word ${!isEditMode ? "cursor-not-allowed opacity-75" : ""}`}
                      style={{ minHeight: "72px", wordWrap: "break-word" }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  Lịch sử trạng thái
                </h4>

                {report.lich_su_trang_thai &&
                report.lich_su_trang_thai.length > 0 ? (
                  <div className="space-y-4">
                    {report.lich_su_trang_thai.map((item, index) => {
                      const statusStyle = getStatusStyle(item.ten);
                      const isExtensionStatus =
                        item.ten === "Đã gia hạn" ||
                        item.ten?.toLowerCase().includes("gia hạn");
                      const extReason = isExtensionStatus ? getExtensionReason(item) : null;

                      return (
                        <div
                          key={item.id || index}
                          className="p-3 bg-gray-50 rounded-md space-y-2"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            {item.ten && (
                              <span
                                className="px-3 py-1 text-xs font-medium rounded-full"
                                style={{
                                  backgroundColor: statusStyle.bg,
                                  color: statusStyle.color,
                                }}
                              >
                                {item.ten}
                              </span>
                            )}
                            <span className="flex items-center gap-1 text-sm text-gray-600">
                              <Clock4 className="w-4 h-4" />
                              {formatDate(item.thoi_gian_tao)}
                            </span>
                          </div>

                          {item.nguoi_dung && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <User className="w-4 h-4" />
                              <span>
                                {item.nguoi_dung.ho_va_ten ||
                                  item.nguoi_dung.ten_dang_nhap ||
                                  "Đang tải..."}
                              </span>
                            </div>
                          )}

                          {isExtensionStatus && (
                            <div className="p-2 bg-amber-50 border border-amber-200/80 rounded-md text-xs text-amber-900 flex items-start gap-1.5">
                              <FileText className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div className="break-words overflow-wrap-anywhere">
                                <span className="font-semibold text-amber-900">Lý do xin gia hạn: </span>
                                <span>{extReason || "Chưa cập nhật lý do xin gia hạn"}</span>
                              </div>
                            </div>
                          )}

                          {item.ghi_chu && !isExtensionStatus && (
                            <div className="flex items-start gap-2 text-sm text-gray-700">
                              <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <span className="break-words overflow-wrap-anywhere">
                                {item.ghi_chu}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    Không có lịch sử cập nhật
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500">Không có dữ liệu</p>
        </div>
      )}
    </BaseModal>
  );
};

export default ReportDetailModal;
